import { useEffect, useState } from "react";
import { XIcon } from "@heroicons/react/outline";
import { createDocument, updateDocument } from "../../api/documentsApi";
import { DOCUMENT_TYPES } from "../../constants";

const DocumentAddModal = ({
  isOpen,
  onClose,
  initialData = null,
  onSuccess,
  onPdfEditor,
}) => {
  const [form, setForm] = useState({
    name: "",
    doc_type: "",
    pdf: null,
    currentFile: "",
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const handleSubmit = async () => {
    if (!form.name || !form.doc_type) {
      setError("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const formData = new FormData();
      formData.append("title", form.name);
      formData.append("type", form.doc_type);

      // Only append file if user selected new one
      if (form.pdf) {
        formData.append("file", form.pdf);
      }

      formData.append("is_active", true);

      if (initialData) {
        // EDIT MODE
        await updateDocument(initialData.id, formData);
      } else {
        // CREATE MODE
        if (!form.pdf) {
          setError("Please upload a PDF.");
          return;
        }
        const created_doc = await createDocument(formData);
        onPdfEditor(created_doc);
      }
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err.response?.data);
      setError(err?.response?.data?.detail || "Failed to save document.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (initialData) {
      setForm({
        name: initialData.title || "",
        doc_type: initialData.type || "",
        pdf: null,
        currentFile: initialData.file || "",
      });
    } else {
      setForm({
        name: "",
        doc_type: "",
        pdf: null,
        currentFile: "",
      });
    }

    setError("");
  }, [isOpen, initialData]);

  if (!isOpen) return;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="w-full max-w-lg bg-slate-900 border border-white/10 rounded-xl shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Upload Pdf</h2>

          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-4">
          {/* Item Name */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Document Name
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white
                focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            />
          </div>

          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Document Type
            </label>
            <select
              value={form.doc_type || ""}
              onChange={(e) =>
                handleChange("doc_type", e.target.value ? e.target.value : null)
              }
              className="w-full bg-[#131C31] border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-pink-500/40"
            >
              <option value="">Select document type</option>

              {DOCUMENT_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* PDF */}
          <div>
            <label className="block text-sm text-slate-300 mb-1">
              Upload PDF
            </label>
            <input
              type="file"
              accept="application/pdf"
              onChange={(e) => handleChange("pdf", e.target.files[0])}
              className="w-full text-sm text-slate-400"
            />
          </div>
          {initialData && form.currentFile && (
            <div className="text-xs text-slate-400">
              Current file:{" "}
              <a
                href={form.currentFile}
                target="_blank"
                rel="noopener noreferrer"
                className="text-pink-400 hover:underline"
              >
                View PDF
              </a>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-2 rounded-lg">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg text-slate-300 hover:bg-slate-800 transition"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg text-white font-medium transition
              ${
                loading
                  ? "bg-pink-400 cursor-not-allowed"
                  : "bg-pink-600 hover:bg-pink-700"
              }
            `}
          >
            {loading
              ? "Saving..."
              : initialData
                ? "Update Document"
                : "Add Pdf"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DocumentAddModal;
